// ** Redux
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// ** Third party
import i18n from "config/i18n";
import cookie from "js-cookie";

// ** Helpers
import { createApiCall } from "helpers/createApiCall";

// ** Configuration
import config from "configuration";

// Globalny Slice zawierający statystyki strony, user-agenta użytkownika, wybrany język, dark-mode itp.
// Bez informacji na temat zalogowanego użytkownika - je umieszczamy w global-user.slice.js

// ############################################################

export const getGlobalStatistics = createAsyncThunk(
	"globalCommon/getGlobalStatistics",
	createApiCall(async (_, client) => {
		const response = await client.get(`/datezone/statistics/main`);
		return { result: response.data };
	})
);

export const setLanguageAsync = createAsyncThunk(
	"globalCommon/setLanguage",
	createApiCall(async ({ language, auth, setCookie }, client) => {
		const finalLanguage = language ? language.slice(0, 2) : "pl";

		cookie.set("datezone_language_pref", finalLanguage, {
			path: "/",
			expires: 365,
		});

		if (auth) {
			await i18n.changeLanguage(finalLanguage);
			await client.put(`/setting/language`, null, {
				params: {
					lang: finalLanguage,
				},
			});
		} else {
			client.modifyLanguageHeader(finalLanguage);
			await i18n.changeLanguage(finalLanguage);
		}

		return { language: finalLanguage, setCookie };
	})
);

// ############################################################

// Initial state
const initialState = {
	userAgent: {
		ua: "",
		browser: {
			name: "",
			version: "",
			major: "",
		},
		engine: {
			name: "",
			version: "",
		},
		os: {
			name: "",
			version: "",
		},
		device: {
			vendor: "",
			model: "",
			type: "",
		},
		cpu: {
			architecture: "",
		},
	},
	statistics: {
		usersOnline: 0,
		usersTotal: 0,
		mediaTotal: 0,
	},
	darkMode: {
		isDarkMode: false,
		isChangedByUser: false,
		isSetByBrowser: false,
	},
	language: "pl",
	isClient: false,
	isAuth: false,
	cookieConsent: {
		showBanner: false,
		showCustomization: false,
	},
	oauth: {
		isActive: false,
	},
};

// ############################################################

// Slice
const globalCommonSlice = createSlice({
	name: "global/common",
	initialState,
	reducers: {
		// ** User Agent
		setUserAgent: (state, action) => {
			state.userAgent.ua = action.payload.ua;
			state.userAgent.browser = action.payload.browser;
			state.userAgent.engine = action.payload.engine;
			state.userAgent.os = action.payload.os;
			state.userAgent.device = action.payload.device;
			state.userAgent.cpu = action.payload.cpu;
		},
		// ** Global Statistics
		setGlobalStatistics(state, action) {
			state.statistics = {
				usersOnline: action.payload.usersOnline,
				usersTotal: action.payload.usersTotal,
				mediaTotal: action.payload.mediaTotal,
			};
		},
		// ** Dark Mode
		setIsInitialized(state) {
			state.darkMode.isSetByBrowser = true;
		},
		setDarkModeFromBrowser(state, action) {
			state.darkMode.isDarkMode = action.payload.isDarkMode;
			state.darkMode.isSetByBrowser = true;
		},
		setDarkModeByUser(state, action) {
			state.darkMode.isDarkMode = action.payload.isDarkMode;
			state.darkMode.isChangedByUser =
				action.payload.isChangedByUser || true;
			state.darkMode.isSetByBrowser = true;
		},
		// ** Language
		setLanguageManually(state, action) {
			state.language = action.payload.language;
		},
		// ** Is Client
		setIsClient(state, action) {
			state.isClient = action.payload;
		},
		setCookieConsentBannerVisibility: (state, action) => {
			state.cookieConsent.showBanner = action.payload.showBanner;
			state.cookieConsent.showCustomization =
				action.payload.showCustomization;
		},
		// ** Is Auth
		setIsAuth(state, action) {
			state.isAuth = action.payload;
		},

		// ** OAuth
		setActiveOauth(state, action) {
			state.oauth.isActive = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Get global statistics
			.addCase(getGlobalStatistics.fulfilled, (state, action) => {
				state.statistics = {
					...state.statistics,
					usersTotal: action.payload.users,
					mediaTotal: action.payload.media,
				};
			})
			// Set language
			.addCase(setLanguageAsync.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(setLanguageAsync.fulfilled, (state, action) => {
				state.isLoading = false;
				state.language = action.payload.language;

				if (action.payload.setCookie) {
					cookie.set(
						config.language.cookieName,
						action.payload.language,
						config.language.cookieOptionsJsCookie
					);
				}
			})
			.addCase(setLanguageAsync.rejected, (state, action) => {
				state.isLoading = false;
				state.error =
					action.payload || "Wystąpił błąd podczas zmiany języka";
			});
	},
});

// ############################################################

export default globalCommonSlice.reducer;

// ** Actions
export const {
	setUserAgent,
	setGlobalStatistics,
	setDarkModeFromBrowser,
	setDarkModeByUser,
	setLanguageManually,
	setIsClient,
	setCookieConsentBannerVisibility,
	setIsAuth,
	setActiveOauth,
} = globalCommonSlice.actions;

// ############################################################
